<template>
    <div
        v-dark
        class="subject-insights__container"
        :class="{ 'subject-insights__container--mock-exam': isMockExam}"
    >
        <div
            v-if="breakpoint === 'black-bear'"
            v-dark
            class="subject-insights__panel"
        >
            <PocketButton
                class="subject-insights__panel-header"
                :class="{ 'subject-insights__panel-header--expanded': showSubjectsPanel }"
                type="tertiary-small"
                :is-dark-mode="isDarkMode"
                :aria-expanded="showSubjectsPanel ? 'true' : 'false'"
                @click="showSubjectsPanel = !showSubjectsPanel"
            >
                <Icon
                    v-dark
                    type="subject"
                    class="subject-insights__panel-icon"
                />
                <span v-dark class="subject-insights__panel-title">
                    {{ subjects.length }} Subject Scores
                </span>
                <Icon
                    v-dark
                    type="accordionArrow"
                    class="subject-insights__subject-expand-arrow"
                    :class="{ 'subject-insights__subject-expand-arrow--expanded': showSubjectsPanel }"
                />
            </PocketButton>
            <template v-if="showSubjectsPanel">
                <SubjectCard
                    v-for="subject in subjects"
                    :key="subject.id"
                    :subject="subject"
                    variant="row"
                />
            </template>
        </div>
        <Table
            v-else
            :key="String(isLoading)"
            class="subject-insights"
            :class="{ 'subject-insights--is-mock-exam': isMockExam}"
            theme="open"
            :show-header="false"
            :rows="expandSubjects ? subjects : subjects.slice(0, numSubjectsVisible)"
            :columns="tableColumns"
            :table-column-labels-styles="tableColumnLabelsStyles"
            :table-row-styles="tableRowStyles"
            :default-sort="defaultSort || undefined"
            :is-dark-mode="isDarkMode"
            @sort="updateSortSettings"
        >
            <template #tableColumnLabelText="{ column }">
                <template v-if="column.propName === 'name'">
                    <div class="subject-insights__column-label-large">
                        <Icon
                            v-dark
                            type="subject"
                            class="subject-insights__column-label-large-icon"
                        />
                        <h2 v-dark class="subject-insights__column-label-large-text">
                            Subject Scores
                        </h2>
                    </div>
                </template>
            </template>
            <template #tableCellValue="{ row, column }">
                <template v-if="column.propName === 'name'">
                    <div v-dark class="subject-insights__cell-name">
                        <OverflowTooltip>
                            {{ row.name }}
                        </OverflowTooltip>
                    </div>
                </template>
                <template v-if="column.propName === 'correctAnswered'">
                    <div v-dark class="subject-insights__cell-correct-answered">
                        {{ row.correctCount }} <span>/</span> {{ row.answeredCount }}
                    </div>
                </template>
                <template v-if="column.propName === 'score'">
                    <div class="subject-insights__cell-score">
                        <div class="subject-insights__cell-score-percent">
                            <template v-if="row.answeredCount">
                                {{ Math.round((row.correctCount / row.answeredCount) * 100) }}
                            </template>
                            <template v-else>
                                - -
                            </template>
                            <span> %</span>
                        </div>
                        <div v-dark class="subject-insights__cell-score-bar">
                            <div
                                :key="`${row.correctCount / row.answeredCount}_${row.name}`"
                                v-dark
                                class="subject-insights__cell-score-bar-fill"
                                :class="{
                                    [`subject-insights__cell-score-bar-fill--${
                                        getScoreRange(Math.round((row.correctCount / row.answeredCount) * 100))
                                    }`]: true
                                }"
                                :style="{ width: `${Math.round((row.correctCount / row.answeredCount) * 100)}%` }"
                            />
                        </div>
                    </div>
                </template>
            </template>
        </Table>
        <PocketButton
            v-if="subjects.length > numSubjectsVisible && breakpoint !== 'black-bear'"
            type="tertiary-small"
            class="subject-insights__expand"
            :class="{ 'subject-insights__expand--expanded': expandSubjects }"
            :is-dark-mode="isDarkMode"
            :aria-label="`
                ${ expandSubjects ? 'Hide' : 'View' } ${ subjects.length - numSubjectsVisible }
                ${ expandSubjects ? `subject${subjects.length > 9 ? 's' : ''}` : 'more' }
            `"
            @click="expandSubjects = !expandSubjects"
        >
            {{ expandSubjects ? 'Hide' : 'View' }} {{ subjects.length - numSubjectsVisible }}
            {{ expandSubjects ? `subject${subjects.length > 9 ? 's' : ''}` : 'more' }}
            <Icon type="accordionArrow" />
        </PocketButton>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { examMetadataModule } from '@/store/examMetadata/module'
import { questionModule } from '@/store/question/module'
import { quizModule } from '@/store/quiz/module'
import { userModule } from '@/store/user/module'
import UIKit, { type ITableSortSettings } from '@pocketprep/ui-kit'
import { getScoreRange } from '@/utils'
import SubjectCard, { type TSubject } from '@/components/Stats/SubjectCard.vue'
import { screenModule } from '@/store/screen/module'

const {
    Table,
    Icon,
    Button: PocketButton,
    OverflowTooltip,
} = UIKit

type TSubjectLib = {
    [subjectName: string]: TSubject & { id: string }
}
type TMockExamSubjects = {
    id: string
    name: string
    correctCount: number
    answeredCount: number
}

const props = withDefaults(defineProps<{
    quizId?: string | null
    numSubjectsVisible?: number
    isMockExam?: boolean
    mockExamSubjectInsights?: TMockExamSubjects[] | null
}>(), {
    quizId: null,
    numSubjectsVisible: 8,
    isMockExam: false,
    mockExamSubjectInsights: null,
})

const isLoading = ref(true)
const sortSettings = ref<ITableSortSettings | null>(null)
const defaultSort = ref<ITableSortSettings | null>(null)
const expandSubjects = ref(false)
const showSubjectsPanel = ref(true)

const tableColumns = computed(() => {
    return [{
        name: 'Subject Scores',
        propName: 'name',
    }, {
        name: props.isMockExam ? 'Correct / Total' : 'Correct / Answered',
        propName: 'correctAnswered',
        isSortDisabled: true,
        styles: { textAlign: 'right', whiteSpace: 'nowrap' },
    }, {
        name: 'Score',
        propName: 'score',
    }]
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const tableGridColumns = computed(() => {
    return {
        'grid-template-columns': `minmax(0, 1fr) 128px ${breakpoint.value === 'brown-bear' ? 228 : 293}px`,
        'column-gap': '20px',
    }
})

const tableColumnLabelsStyles = computed(() => {
    return props.isMockExam
        ? undefined
        : { ...tableGridColumns.value, padding: '0 0 7px 6px' }
})

const tableRowStyles = computed(() => {
    return props.isMockExam
        ? undefined
        : { ...tableGridColumns.value, padding: '10px 0 9px 6px' }
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const subjects = computed(() => {
    const examSubjects = examMetadataModule.state.subjectsWithLevels
    if (!examSubjects) {
        return []
    }
    const defaultSubjectLib = examSubjects.reduce<TSubjectLib>((acc, s) => {
        acc[s.subjectId] = {
            id: s.subjectId,
            name: s.subjectName,
            correctCount: 0,
            answeredCount: 0,
        }
        return acc
    }, {})

    const answers = props.quizId
        ? quizModule.getters.getLatestAnswers({ quizId: props.quizId, questionFilter: 'all' })
        : quizModule.getters.getLatestAnswers({ questionFilter: 'all' })

    const serialQuestionInfoLib = questionModule.getters.getSerialQuestionInfoLib({ questionFilter: 'all' })

    const subjectLib = Object.entries(answers).reduce<TSubjectLib>((acc, [ serial, answer ]) => {
        const subjectId = serialQuestionInfoLib[serial]?.subjectId
        if (!subjectId) {
            return acc
        }
        if (!(subjectId in acc)) {
            return acc
        }
        const subjectObject = acc[subjectId]
        if (subjectObject) {

            subjectObject.correctCount = (subjectObject.correctCount as number) + (answer.isCorrect ? 1 : 0)
            subjectObject.answeredCount = (subjectObject.answeredCount as number) + 1
        }

        return acc
    }, defaultSubjectLib)

    const subjectInsightType = props.isMockExam ? props.mockExamSubjectInsights : subjectLib

    return Object.values(subjectInsightType || [])
        .sort((a, b) => {
            const sort = sortSettings.value

            if (sort?.column?.propName === 'name') {
                return sort.direction * a.name.localeCompare(b.name)
            }
            if (sort?.column?.propName === 'correctAnswered') {
                return sort.direction * (a.correctCount - b.correctCount)
            }
            if (sort?.column?.propName === 'score') {
                const aScore = (a.correctCount / a.answeredCount) * 100
                const bScore = (b.correctCount / b.answeredCount) * 100
                return isNaN(aScore)
                    ? 1
                    : isNaN(bScore)
                        ? -1
                        : sort.direction * (bScore - aScore)
            }

            return 0
        })
})

onMounted(async () => {
    await Promise.all([
        questionModule.actions.fetchSerialQuestionInfoLib(),
        userModule.actions.fetchUserData(),
        examMetadataModule.actions.fetchExamMetadata(),
    ])

    const sortPreference = userModule.state.settings.subjectInsightsSort

    if (sortPreference) {
        defaultSort.value = sortPreference === 'alphabetical'
            ? { column: { propName: 'name', name: 'Name' }, direction: 1 }
            : sortPreference === 'weakest'
                ? { column: { propName: 'score', name: 'Score' }, direction: -1 }
                : { column: { propName: 'score', name: 'Score' }, direction: 1 }
        sortSettings.value = JSON.parse(JSON.stringify(defaultSort.value))
    }

    isLoading.value = false
})

const updateSortSettings = (sortSettingsParam: ITableSortSettings) => {
    sortSettings.value = sortSettingsParam

    if (sortSettings.value.direction === 0) {
        sortSettings.value.direction = 1
    }
}
</script>

<style lang="scss" scoped>
.subject-insights {
    :deep(.uikit-table__column-labels) {
        border-bottom-width: 2px;
    }

    :deep(.uikit-table__column-labels--dark) {
        border-bottom-color: rgba($slate, 0.8);
    }

    :deep(.uikit-table__column-label) {
        &:nth-child(1) {
            svg.uikit-table__sort-icon {
                position: absolute;
                top: 7px;
                right: -18px;
            }

            .uikit-table__column-label-content::before {
                width: calc(100% + 28px);
            }
        }

        &:nth-child(2) {
            svg {
                position: absolute;
                top: 3px;
                right: -15px;
            }

            .uikit-table__column-label-content::before {
                width: calc(100% + 22px);
            }
        }

        &:nth-child(2),
        &:nth-child(3) {
            margin-top: 4px;
        }
    }

    &:not(.subject-insights--is-mock-exam) {
        :deep(.uikit-table__column-label:nth-child(1) .uikit-table__column-label-content) {
            display: flex;
            width: fit-content;
        }
    }

    &--is-mock-exam {
        :deep(.uikit-table__column-labels),
        :deep(.uikit-table__row) {
            padding-left: 8px;
            padding-right: 22px;
        }

        :deep(.uikit-table__column-label) {
            &:nth-child(1) {
                svg.uikit-table__sort-icon {
                    position: absolute;
                    top: 7px;
                    right: -18px;
                }

                .uikit-table__column-label-content::before {
                    width: calc(100% + 28px);
                }
            }

            &:nth-child(2) {
                svg {
                    position: absolute;
                    top: 3px;
                    right: -15px;
                }

                .uikit-table__column-label-content::before {
                    width: calc(100% + 22px);
                }
            }

            &:nth-child(2),
            &:nth-child(3) {
                margin-top: 4px;
            }
        }

        :deep(.uikit-table__column-label),
        :deep(.uikit-table__cell) {
            &:nth-child(1) {
                width: 400px;

                @include breakpoint(brown-bear) {
                    width: 318px;
                }
            }

            &:nth-child(2) {
                width: 90px;
                text-align: right;
                margin-right: 24px;

                @include breakpoint(brown-bear) {
                    width: 111px;
                    margin-right: 20px;
                }
            }

            &:nth-child(3) {
                width: 230px;

                @include breakpoint(brown-bear) {
                    width: 200px;
                }
            }
        }
    }

    &__expand {
        padding: 10px 11px;

        svg {
            margin-left: 3px;
        }

        &--expanded {
            svg {
                transform: rotate(180deg);
            }
        }
    }

    &__container {
        width: 974px;
        background: $white;
        border: 1px solid $gray-divider;
        border-radius: 6px;
        padding: 11px 14px;
        margin: 21px auto 18px;
        box-sizing: border-box;
        position: relative;

        @include breakpoint(brown-bear) {
            width: calc(100% - 44px);
        }

        @include breakpoint(black-bear) {
            width: 100%;
            background: transparent;
            padding: 0 22px;
            margin: 25px auto 18px;
            border: none;
        }

        &--dark {
            background: $brand-black;
            border-color: $slate;

            @include breakpoint(black-bear) {
                background: transparent;
                border: none;
            }
        }

        &--mock-exam {
            &--dark {
                background-color: $brand-black;
            }
        }
    }

    &__panel {
        position: relative;
        background: $white;
        border: 0.5px solid rgba($pewter, 0.85);
        border-radius: 5px;
        box-shadow: 0 1px 4px 0 rgba($ash, 0.3);
        overflow: hidden;

        &--dark {
            background: $brand-black;
            border-color: $slate;
            box-shadow: 0 1px 4px 0 $jet;
        }
    }

    &__panel-header {
        display: block;
        box-sizing: border-box;
        width: 100%;
        height: 50px;
        padding: 0 15px;
        text-align: left;

        :deep(.uikit-btn__content) {
            display: flex;
            align-items: center;
            gap: 6px;
            height: 100%;
        }

        &:enabled:hover {
            text-decoration: none;
        }

        &:enabled:focus::before {
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            border-radius: 5px 5px 0 0;
        }

        &--expanded::after {
            content: '';
            position: absolute;
            left: 15px;
            right: 15px;
            bottom: 0;
            height: 2px;
            background: $gray-divider;
        }

        &--expanded--dark::after {
            background: $pewter;
        }
    }

    &__panel-icon {
        width: 22px;
        height: 21px;
        flex-shrink: 0;
        color: $slate;

        &--dark {
            color: $fog;
        }
    }

    &__panel-title {
        font-size: 15px;
        line-height: 20px;
        font-weight: 600;
        color: $brand-black;

        &--dark {
            color: $fog;
        }
    }

    &__subject-expand-arrow {
        width: 12px;
        height: 7px;
        color: $brand-blue;

        &--expanded {
            transform: rotate(180deg);
        }

        &--dark {
            color: $banana-bread;
        }
    }

    &__column-label-large {
        display: flex;
        align-items: center;
    }

    &__column-label-large-icon {
        height: 23px;
        margin-right: 8px;
        color: $slate;

        &--dark {
            color: $fog;
        }
    }

    &__column-label-large-text {
        font-size: 16px;
        line-height: 22px;
        font-weight: 600;
        color: $brand-black;
        margin: 0;

        &--dark {
            color: $white;
        }
    }

    &__cell-name {
        font-size: 15px;
        line-height: 20px;
        color: $brand-black;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        &--dark {
            color: $white;
        }
    }

    &__cell-correct-answered {
        font-size: 16px;
        letter-spacing: -0.3px;
        font-weight: 400;
        color: $slate-03;

        &--dark {
            color: $white;
        }

        span {
            font-weight: 500;
            letter-spacing: 0.43px;
            color: $pewter;
        }
    }

    &__cell-score-percent {
        font-size: 18px;
        line-height: 19px;
        letter-spacing: -0.3px;
        font-weight: 600;
        margin-right: 12px;
        min-width: 51px;
        flex-shrink: 0;
        text-align: right;

        span {
            letter-spacing: 0.48px;
            font-weight: 400;
            margin-left: -2px;
        }
    }

    &__cell-score {
        display: flex;
        align-items: center;
    }

    &__cell-score-bar {
        background: $gray-progress-background;
        width: 100%;
        height: 14px;
        border-radius: 2px;
        position: relative;
        border: 1px solid rgba($pewter, 0.85);

        &--dark {
            background: $moonlit-ocean;
            border-color: $slate;
        }
    }

    &__cell-score-bar-fill {
        position: absolute;
        top: -1px;
        height: 14px;
        border-radius: 2px;
        border: 1px solid $pepper;

        &--low {
            background: $tomato;

            &--dark {
                background: $brand-red;
                border: none;
            }
        }

        &--medium {
            background: $cheddar;
            border-color: $golden-hind;

            &--dark {
                background: $banana-bread;
                border: none;
            }
        }

        &--high {
            background: $field;
            border-color: $spectral-green;

            &--dark {
                background: $jungle-green;
                border: none;
            }
        }
    }

}
</style>
